import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FormControl, Grid, Typography } from "@mui/material";
import { InputLabel, InputBase, HelperText } from "common/styles/muiStyled";
import { OutlinedInputProps } from "@mui/material/OutlinedInput";
import colors from "../../../../../common/styles/colors";

export interface CustomInputProps extends Partial<OutlinedInputProps> {
  name: string;
  value: string;
  maxLength?: number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  setFieldValue?: (field: string, value: string) => void;
  helperText?: React.ReactNode;
  error?: boolean;
  optional?: boolean;
  type?: string;
  disabled?: boolean;
  label?: string;
  expandSubtitleText?: string;
  onExpandClick?: () => void;
  tipText?: string;
  gridSm?: number;
}

const Input: React.FC<CustomInputProps> = ({
  name,
  value,
  maxLength,
  onChange,
  onBlur,
  setFieldValue,
  helperText,
  error,
  optional,
  type,
  disabled,
  label,
  expandSubtitleText,
  onExpandClick,
  tipText,
  gridSm,
  ...rest
}) => {
  const { t } = useTranslation();

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const trimmedValue = event.target.value.trim();

      if (event.target.value !== trimmedValue && setFieldValue) {
        setFieldValue(name, trimmedValue);
      }

      onBlur?.(event);
    },
    [setFieldValue, name, onBlur]
  );

  const labelText = label || t(`common:badgeForm.${name}`);
  const optionalText = tipText || t("common:badgeForm.optional");

  return (
    <Grid item xs={12} sm={gridSm ?? 6} sx={{ pt: 3 }}>
      <FormControl variant="standard" fullWidth>
        <InputLabel>
          {labelText}
          {(optional || tipText) && <span data-testid="optional-badge-field-title">{` (${optionalText})`}</span>}
        </InputLabel>
        <InputBase
          id={`badge-form-${name}`}
          name={name}
          type={type}
          value={value}
          inputProps={{ maxLength }}
          onChange={onChange}
          onBlur={handleBlur}
          error={error}
          disabled={disabled}
          {...rest}
        />
        {helperText && (
          <HelperText sx={{ color: error ? colors.redColor1 : "rgba(0, 0, 0, 0.6)" }} id={`badge-form-${name}`}>
            {helperText}
            {expandSubtitleText && (
              <Typography
                id={`badge-form-${name}-expand`}
                sx={{
                  display: "inline",
                  fontWeight: "bold",
                  fontSize: "inherit",
                  cursor: "pointer"
                }}
                onClick={onExpandClick}
              >
                {" "}
                {expandSubtitleText}
              </Typography>
            )}
          </HelperText>
        )}
      </FormControl>
    </Grid>
  );
};

export default Input;
